import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const PayPalSuccessTour = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');
  const payerId = searchParams.get('PayerID');
  const bookingNumber = searchParams.get('booking_number');

  useEffect(() => {
    if (token && payerId && bookingNumber) {
      verifyPayment();
    } else {
      setError('Invalid payment parameters');
      setLoading(false);
    }
  }, [token, payerId, bookingNumber]);

  const verifyPayment = async () => {
    try {
      console.log('Verifying PayPal payment for tour...', { token, payerId, bookingNumber });

      // Get PayPal configuration
      const { data: paypalMethod, error: paypalError } = await supabase
        .from('payment_methods')
        .select('settings, is_sandbox')
        .eq('method_type', 'paypal')
        .eq('is_active', true)
        .single();

      if (paypalError || !paypalMethod) {
        throw new Error('PayPal configuration not found');
      }

      const { settings, is_sandbox } = paypalMethod;
      
      if (!(settings as any)?.client_id || !(settings as any)?.client_secret) {
        throw new Error('PayPal credentials not configured');
      }

      // Call verify payment edge function
      const { data, error } = await supabase.functions.invoke('verify-paypal-payment', {
        body: {
          order_id: token,
          payer_id: payerId,
          booking_number: bookingNumber,
          is_sandbox,
          client_id: (settings as any).client_id,
          client_secret: (settings as any).client_secret
        }
      });

      if (error) {
        console.error('Error verifying payment:', error);
        throw new Error(error.message || 'Payment verification failed');
      }

      if (data?.success) {
        console.log('Payment verified successfully');
        
        // Get updated booking data
        const { data: booking, error: bookingError } = await supabase
          .from('tour_bookings')
          .select('*')
          .eq('booking_number', bookingNumber)
          .single();

        if (bookingError) {
          console.error('Error fetching booking:', bookingError);
        } else {
          setBookingData(booking);
        }

        setSuccess(true);
        toast({
          title: "Payment Successful!",
          description: "Your tour booking has been confirmed and you will receive an email confirmation shortly.",
        });
      } else {
        throw new Error(data?.error || 'Payment verification failed');
      }
    } catch (err: any) {
      console.error('Payment verification error:', err);
      setError(err.message || 'Payment verification failed');
      toast({
        title: "Payment Verification Failed",
        description: err.message || "There was an error verifying your payment.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
                <h2 className="text-2xl font-bold mb-2">Verifying Payment</h2>
                <p className="text-muted-foreground">
                  Please wait while we verify your PayPal payment...
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-8 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
                <h2 className="text-2xl font-bold mb-2 text-destructive">Payment Verification Failed</h2>
                <p className="text-muted-foreground mb-6">
                  {error}
                </p>
                <div className="space-y-4">
                  <Button onClick={() => navigate('/contact')} variant="outline">
                    Contact Support
                  </Button>
                  <Button onClick={() => navigate('/')} className="w-full">
                    Return Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
                <CheckCircle className="h-12 w-12 text-primary" />
              </div>
              <CardTitle className="text-2xl text-primary">Payment Successful!</CardTitle>
              <p className="text-muted-foreground">
                Your tour booking has been confirmed
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {bookingData && (
                <>
                  <div className="text-center">
                    <Badge variant="secondary" className="text-lg px-4 py-2">
                      Booking #{bookingData.booking_number}
                    </Badge>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h3 className="font-semibold">Booking Details</h3>
                    <div className="grid gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Customer:</span>
                        <span className="font-medium">{bookingData.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email:</span>
                        <span className="font-medium">{bookingData.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tour:</span>
                        <span className="font-medium">{bookingData.tour_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="font-medium">{bookingData.booking_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Time:</span>
                        <span className="font-medium">{bookingData.tour_schedule}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Adults:</span>
                        <span className="font-medium">{bookingData.adults}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pickup Point:</span>
                        <span className="font-medium">{bookingData.pickup_point}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total Paid:</span>
                        <span className="text-primary">${bookingData.total_price}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">What's Next?</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• You will receive a confirmation email shortly</li>
                      <li>• Please arrive 15 minutes before your scheduled time</li>
                      <li>• Bring comfortable clothes and sunscreen</li>
                      <li>• Keep your booking number handy: <strong>{bookingData.booking_number}</strong></li>
                      <li>• Contact us if you have any questions</li>
                    </ul>
                  </div>
                </>
              )}

              <div className="flex gap-4">
                <Button onClick={() => navigate('/')} className="flex-1">
                  Return Home
                </Button>
                <Button onClick={() => navigate('/contact')} variant="outline" className="flex-1">
                  Contact Us
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default PayPalSuccessTour;